import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Home, TrendingUp, Eye, Lock, Sparkles, Plus, Calculator,
    Handshake, Users, Coins, Loader2, BadgeCheck, ArrowRight, Settings2,
    Building2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import Stat from "@/components/agent-console/Stat";
import ListingRow from "@/components/agent-console/ListingRow";
import ConvertToJVDialog from "@/components/agent-console/ConvertToJVDialog";
import VisibilityDialog from "@/components/agent-console/VisibilityDialog";
import MatchesDialog from "@/components/agent-console/MatchesDialog";
import UpgradeTierDialog from "@/components/agent-console/UpgradeTierDialog";

export default function AgentConsole() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [convertOpen, setConvertOpen] = useState(null); // listing being converted
    const [visibilityOpen, setVisibilityOpen] = useState(null); // listing being toggled
    const [matchesOpen, setMatchesOpen] = useState(null); // listing being matched
    const [tierOpen, setTierOpen] = useState(false);

    const isAgent = user && (
        (user.roles || []).includes("agent")
        || (user.services || []).includes("agent")
        || (user.roles || []).includes("admin")
    );

    const reload = async () => {
        setLoading(true);
        try {
            const res = await api.get("/agent/console");
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load console");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) {
            navigate("/login?next=/agent-console");
            return;
        }
        if (!isAgent) {
            toast.error("Agent access only");
            navigate("/");
            return;
        }
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (!user || !isAgent) return null;

    return (
        <div data-testid="agent-console-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="bg-ocean-deep-grad text-white py-12 lg:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-4">
                        <Settings2 className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            Agent Console
                        </span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
                        Welcome back, <span className="italic text-turquoise">{user.full_name?.split(" ")[0] || "Agent"}</span>.
                    </h1>
                    <p className="font-app text-sm sm:text-base text-white/85 max-w-3xl">
                        Your listings, conversion telemetry, and one-click JV pipeline. Everything you need to turn
                        a sale into a partnership.
                    </p>
                </div>
            </section>

            {/* QUICK ACTIONS */}
            <section className="py-6 border-b border-border bg-card">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-2">
                    <Button asChild data-testid="agent-console-new-listing-btn" className="bg-coral hover:bg-coral-deep text-white font-app">
                        <Link to="/listings/new">
                            <Plus className="h-4 w-4 mr-2" /> New listing
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-bulk-btn" className="font-app">
                        <Link to="/listings/bulk">
                            Bulk upload (CSV)
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-explain-btn" className="font-app">
                        <Link to="/agents/explain-jv">
                            Explain JV (60s)
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-calc-btn" className="font-app">
                        <Link to="/agents/jv-calculator">
                            <Calculator className="h-4 w-4 mr-2" /> JV calculator
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-training-btn" className="font-app">
                        <Link to="/agents/training">
                            <BadgeCheck className="h-4 w-4 mr-2" /> Training & certification
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-payouts-btn" className="font-app">
                        <Link to="/agent/payouts">
                            <Coins className="h-4 w-4 mr-2" /> My payouts
                        </Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="agent-console-agency-btn" className="font-app">
                        <Link to="/agency">
                            <Building2 className="h-4 w-4 mr-2" /> My agency
                        </Link>
                    </Button>
                </div>
            </section>

            <section className="py-10 lg:py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading && (
                        <div className="text-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-coral mx-auto mb-3" />
                            <p className="font-app text-sm text-muted-foreground">Loading…</p>
                        </div>
                    )}

                    {data && (
                        <>
                            {/* TELEMETRY */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="agent-console-stats">
                                <Stat label="Active listings" value={data.summary.listings_active} icon={Home} />
                                <Stat label="Total views" value={data.summary.total_views.toLocaleString()} icon={Eye} accent />
                                <Stat label="JV submissions" value={data.summary.jvs_total} icon={Handshake} />
                                <Stat label="Closed deals" value={data.summary.closed_deals} icon={Sparkles} accent />
                                <Stat label="Off-market" value={data.summary.listings_off_market} icon={Lock} />
                                <Stat label="JV-converted" value={data.summary.listings_converted_to_jv} icon={TrendingUp} />
                                <Stat label="Lead inbox" value={data.summary.leads} icon={Users} />
                                <Stat label="Concierge referrals" value={data.summary.concierge_referrals} icon={Coins} />
                            </div>

                            {/* TIER STATUS */}
                            <Card className="p-5 mb-8 bg-fiji-green/5 dark:bg-fiji-green/10 border-fiji-green/30" data-testid="agent-console-tier-card">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <BadgeCheck className={`h-6 w-6 ${data.summary.agent_verified ? "text-fiji-green" : "text-muted-foreground"}`} />
                                        <div>
                                            <div className="font-display text-base font-bold text-foreground">
                                                {data.summary.agent_verified ? "Verified Agent" : "Unverified"} ·{" "}
                                                <span className="capitalize">{data.summary.agent_tier}</span> tier
                                            </div>
                                            <p className="font-app text-xs text-muted-foreground">
                                                {data.summary.agent_verified
                                                    ? "Your verification badge appears on every listing card."
                                                    : "Verification adds a trust badge investors filter on."}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid="agent-console-upgrade-tier-btn"
                                        onClick={() => setTierOpen(true)}
                                        className="font-app"
                                    >
                                        Upgrade <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                    </Button>
                                </div>
                            </Card>

                            {/* LISTINGS TABLE */}
                            <h2 className="font-display text-xl lg:text-2xl font-bold text-foreground mb-4">
                                My listings
                            </h2>
                            {(data.listings || []).length === 0 ? (
                                <Card className="p-8 text-center bg-card">
                                    <p className="font-app text-sm text-muted-foreground mb-3">No listings yet.</p>
                                    <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                                        <Link to="/listings/new">Create your first listing</Link>
                                    </Button>
                                </Card>
                            ) : (
                                <div className="space-y-3" data-testid="agent-console-listings">
                                    {(data.listings || []).map((l) => (
                                        <ListingRow
                                            key={l.id}
                                            listing={l}
                                            onConvert={() => setConvertOpen(l)}
                                            onVisibility={() => setVisibilityOpen(l)}
                                            onMatches={() => setMatchesOpen(l)}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            {convertOpen && (
                <ConvertToJVDialog
                    listing={convertOpen}
                    onClose={() => setConvertOpen(null)}
                    onSuccess={() => {
                        setConvertOpen(null);
                        reload();
                    }}
                />
            )}
            {visibilityOpen && (
                <VisibilityDialog
                    listing={visibilityOpen}
                    onClose={() => setVisibilityOpen(null)}
                    onSuccess={() => {
                        setVisibilityOpen(null);
                        reload();
                    }}
                />
            )}
            {matchesOpen && (
                <MatchesDialog listing={matchesOpen} onClose={() => setMatchesOpen(null)} />
            )}
            {tierOpen && (
                <UpgradeTierDialog user={user} onClose={() => setTierOpen(false)} />
            )}
        </div>
    );
}
